import { createFileRoute, useSearch, Link } from "@tanstack/react-router";
import { dehydrate, hydrate, HydrationBoundary, useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { systemEmailsQueryOptions, systemEmailQueryOptions, deleteSystemEmailFn } from "@/server/emails";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import type { EmailType, EmailLanguage } from "@/lib/mma/types.gen";
import { EditEmailForm } from "../-components/EditEmailForm";
import { PlusIcon } from "lucide-react";
import { ContextUser, hasWritePermission, hasDeletePermission } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/emails/system/")({
  component: SystemEmailIndexComponent,
  validateSearch: (search: Record<string, unknown>) => {
    const validEmailTypes: EmailType[] = [
      "registrationVerificationEmail",
      "registrationConfirmEmail",
      "registrationAdminEmail",
      "resetPasswordEmail",
    ];

    const type =
      typeof search.type === "string" && validEmailTypes.includes(search.type as EmailType)
        ? (search.type as EmailType)
        : undefined;

    const language =
      typeof search.language === "string" && search.language.length > 0
        ? (search.language as EmailLanguage)
        : undefined;

    return { type, language };
  },
  loaderDeps: ({ search }) => ({ type: search.type, language: search.language }),
  loader: async ({ context, deps }) => {
    const { type, language } = deps;

    if (type && language) {
      return {
        crumb: `${type} (${language})`,
        dehydratedState: dehydrate(context.queryClient),
        user: context.user as ContextUser | null | undefined,
      };
    }
    return {
      dehydratedState: dehydrate(context.queryClient),
      user: context.user as ContextUser | null | undefined,
    };
  },
});

function SystemEmailIndexComponent() {
  const search = useSearch({ from: "/_dashboard/emails/system/" });
  const { dehydratedState, user } = Route.useLoaderData();
  const queryClient = useQueryClient();
  hydrate(queryClient, dehydratedState);
  const mayWrite = hasWritePermission(user, "EMAILS");
  const mayDelete = hasDeletePermission(user, "EMAILS");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<{ type: EmailType; language: EmailLanguage; name: string } | null>(
    null
  );
  const toastIdRef = useRef<string | number | null>(null);

  const deleteEmailMutation = useMutation({
    mutationFn: (data: { emailType: EmailType; language: EmailLanguage }) => deleteSystemEmailFn({ data }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["emails", "system"] });
      setDeleteDialogOpen(false);
      setEmailToDelete(null);
      if (toastIdRef.current !== null) {
        toast.success("Email deleted successfully", { id: toastIdRef.current });
      } else {
        toast.success("Email deleted successfully");
      }
    },
    onError: (error: any) => {
      if (toastIdRef.current !== null) {
        toast.error(error?.message || "Failed to delete email", { id: toastIdRef.current });
      } else {
        toast.error(error?.message || "Failed to delete email");
      }
    },
  });

  const hasSearchParams = !!(search.type && search.language);

  const emailsQuery = useQuery(systemEmailsQueryOptions);
  const emails = emailsQuery.data || [];

  const emailQuery = useQuery(
    hasSearchParams
      ? systemEmailQueryOptions(search.type as EmailType, search.language as EmailLanguage)
      : {
          queryKey: ["email", "system", "disabled"],
          queryFn: async () => undefined,
          enabled: false,
        }
  );

  const handleDeleteClick = (email: any) => {
    setEmailToDelete({
      type: email.emailType,
      language: email.language,
      name: email.name,
    });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (emailToDelete) {
      toastIdRef.current = toast.loading("Deleting email...");
      deleteEmailMutation.mutate({
        emailType: emailToDelete.type,
        language: emailToDelete.language,
      });
    }
  };

  return (
    <HydrationBoundary state={dehydratedState}>
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Email Template"
        description={`Are you sure you want to delete "${emailToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        loading={deleteEmailMutation.isPending}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="destructive"
      />

      {!hasSearchParams && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">System Emails</h2>
            {mayWrite && (
              <Button asChild variant={"outline"}>
                <Link to="/emails/system/new-email">
                  <PlusIcon className="w-4 h-4 mr-2" /> Add new email
                </Link>
              </Button>
            )}
          </div>
          {emailsQuery.isLoading ? (
            <Card>
              <CardContent>
                <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Loading...</p>
              </CardContent>
            </Card>
          ) : emails.length === 0 ? (
            <Card>
              <CardContent>
                <p className="text-md pb-20 pt-15 text-center text-muted-foreground">
                  No system email templates configured. Create one to get started.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {emails.map((email: any) => (
                <Card key={email.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base">{email.name}</CardTitle>
                        <p className="text-sm text-muted-foreground ">
                          {email.emailType} · {email.language}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link to="/emails/system" search={{ type: email.emailType, language: email.language }}>
                            Edit
                          </Link>
                        </Button>
                        {mayDelete && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteClick(email)}
                            disabled={deleteEmailMutation.isPending}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {hasSearchParams && emailQuery.isLoading && (
        <Card>
          <CardContent>
            <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Loading email...</p>
          </CardContent>
        </Card>
      )}

      {hasSearchParams && !emailQuery.isLoading && emailQuery.data && (
        <EditEmailForm
          scope="system"
          email={emailQuery.data}
          emailType={search.type}
          emailLanguage={search.language}
          user={user}
        />
      )}

      {hasSearchParams && !emailQuery.isLoading && !emailQuery.data && (
        <Card>
          <CardContent>
            <p className="text-md pb-20 pt-15 text-center text-muted-foreground">Email template not found.</p>
          </CardContent>
        </Card>
      )}
    </HydrationBoundary>
  );
}
